// Room codes (EM-2, D-30): FOUR characters of Crockford base32 without 0/O/1/I (and without L and U), any
// case, shown as they are - the owner asked for four because eight is what he has to read out and type on a
// phone. Dashes and spaces are still forgiven on input: people paste old links and type what they see.
// Links carry the code in the fragment: #/r/7K3M (EM-18).
//
// ONE transform, two users (D-33): `cleanCode` is what the code field does to every keystroke and every
// paste, and `normalizeCode` is that same transform plus the check. They cannot drift apart, and
// roomCode.test.ts reads the server's normalize_code out of its own source to keep them in step with it.
const ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
/** What the server's normalize_code forgives: hyphens and spaces (and, here, any other whitespace a phone
 * may paste - the client normalizes before it sends, so the server never sees one). */
const FORGIVEN = /[-\s]/g;
export const CODE_LEN = 4;

/** The forgiving half: drop what may be pasted, upper-case the rest. Never rejects, never truncates. */
export function cleanCode(raw: string): string {
  return raw.replace(FORGIVEN, "").toUpperCase();
}

export function normalizeCode(raw: string): string | null {
  const code = cleanCode(raw);
  if (code.length !== CODE_LEN || [...code].some((c) => !ALPHABET.includes(c))) return null;
  return code;
}

export function displayCode(code: string): string {
  return code; // D-30: four characters stand on their own, with no hyphen to read out
}

/** The code in a join link's fragment (#/r/CODE), or null. */
export function codeFromHash(hash: string): string | null {
  const m = /^#\/r\/([^/?#]+)$/.exec(hash);
  if (!m) return null;
  const code = normalizeCode(decodeURIComponent(m[1]));
  return code ? displayCode(code) : null;
}

/** MAJLIS-80 (D-15, EM-4, EM-18): the secret in a host link's fragment (#/h/SECRET), or null. Mirrors codeFromHash
 * and the server's own bounds for join.host_secret (32..128 URL-safe base64 characters; the server mints 43). The
 * fragment never reaches a server; the secret is sent only inside the join message and is never logged. */
export function hostSecretFromHash(hash: string): string | null {
  const m = /^#\/h\/([A-Za-z0-9_-]{32,128})$/.exec(hash);
  return m ? m[1] : null;
}

/** MAJLIS-66 (P-33, EM-4, EM-18): the secret in a read link's fragment (#/v/SECRET), or null - the same bounds as the
 * host link's. It never reaches a server in a URL: the page sends it once, in the Authorization header. */
export function readSecretFromHash(hash: string): string | null {
  const m = /^#\/v\/([A-Za-z0-9_-]{32,128})$/.exec(hash);
  return m ? m[1] : null;
}

/** The alphabet and length this file enforces, for the test that reads the server's copy. */
export const CODE_RULE = { alphabet: ALPHABET, len: CODE_LEN, forgiven: FORGIVEN.source } as const;
